/*
 * Privacy Statement content. Modeled on `terms.ts` (typed content module),
 * but extended to support a data-controller/contact block and numbered
 * sections whose bodies interleave paragraphs, bullet lists and tables.
 *
 * Copy is reproduced verbatim from the nsave privacy statement. The source
 * numbering deliberately skips section 5 (1,2,3,4,6,7,8,9,10,11) — this order
 * is preserved as authored below.
 */

/** A single ordered block within a section body. */
export type PrivacyBlock =
  | { type: 'paragraph'; text: string }
  /** A small subheading within a section, e.g. "2.1 Data you give us directly". */
  | { type: 'subheading'; text: string }
  | { type: 'list'; items: string[] }
  /**
   * A simple table. `columns` are the header labels; each row has one cell per
   * column. A single-column table (one header) renders as a bordered list.
   */
  | { type: 'table'; columns: string[]; rows: string[][] };

export interface PrivacySection {
  /** Section number as authored, e.g. "1", "6" (numbering skips 5). */
  number: string;
  title: string;
  body: PrivacyBlock[];
}

export interface PrivacyContactLine {
  label: string;
  value: string;
}

export const privacyContent = {
  title: 'Privacy Statement',
  // No "Last updated" line on the privacy statement, at Compliance's request.
  // Terms & Conditions keeps its own — see `src/app/terms.index.tsx`.
  intro: [
    'At nsave, we believe that being open about how we handle your personal data is the foundation of a trustworthy financial service.',
    'This statement tells you exactly what data we collect, why we need it, how long we keep it, and what rights you have over it — in plain English.',
  ],
  controller: [
    { label: 'Data controller', value: 'nsave Ltd, 124 City Road, London, EC1V 2NX' },
    { label: 'Contact', value: 'support@nsave.com' },
  ] satisfies PrivacyContactLine[],
  sections: [
    {
      number: '1',
      title: 'Who We Are',
      body: [
        {
          type: 'paragraph',
          text: 'Nsave LTD ("nsave", "we", "us") is the data controller for all personal data collected through www.nsave.com and the nsave mobile application. We are registered in England and Wales with our office at 124 City Road, London, EC1V 2NX.',
        },
        {
          type: 'paragraph',
          text: 'Our technology partner, npay Inc., is a Delaware-registered technology services provider. Where data is shared with npay Inc. to deliver our services, this is done under appropriate data transfer safeguards (see Section 6).',
        },
        {
          type: 'paragraph',
          text: 'If you have questions about this statement or your personal data, please contact us at support@nsave.com. Our website is restricted to individuals aged 18 and over with full legal capacity.',
        },
      ],
    },
    {
      number: '2',
      title: 'What Data We Collect',
      body: [
        {
          type: 'paragraph',
          text: 'We only collect data we genuinely need. Here is a clear breakdown:',
        },
        { type: 'subheading', text: '2.1 Data you give us directly' },
        {
          type: 'list',
          items: [
            'Your name, date of birth and address',
            'Your email address (including if you join the nsave waitlist)',
            'Financial data, including your IBAN, account balance and transaction history',
            'Professional information, such as your employer and job title',
            'Family information, such as marital status',
            'Any information you share with us through forms, surveys or support messages',
          ],
        },
        { type: 'subheading', text: '2.2 Data we collect automatically' },
        {
          type: 'paragraph',
          text: 'When you use our website or app, we automatically collect certain technical data to keep things running smoothly and securely:',
        },
        {
          type: 'list',
          items: [
            'Device and browser information (type, version, operating system)',
            'Your IP address and internet service provider',
            'Pages visited, links clicked, and session timestamps',
            'Language preferences',
            'Verification data to confirm you are a human being',
          ],
        },
        { type: 'subheading', text: '2.3 Cookies and similar technologies' },
        {
          type: 'paragraph',
          text: 'We use cookies and similar tracking technologies to improve your experience. When you first visit our site, you will be shown a cookie banner and asked for your consent before any non-essential cookies are placed on your device.',
        },
        {
          type: 'paragraph',
          text: 'You can manage or withdraw your cookie preferences at any time through your browser settings or our cookie preference centre. Please note that disabling certain cookies may affect how some features of the website work. For full details, see our Cookie Policy at nsave.com/cookie-policy.',
        },
      ],
    },
    {
      number: '3',
      title: 'Purposes of processing',
      body: [
        {
          type: 'paragraph',
          text: 'nsave collects, processes and uses data in a lawful way and in good faith. The list below sets out exactly what we use your data for:',
        },
        {
          type: 'list',
          items: [
            'Verifying your identity and setting up your account',
            'Delivering and managing our services to you',
            'Communicating with you about your account',
            'Complying with financial services regulations',
            'Detecting and preventing fraud or security threats',
            'Improving the website and our services',
            'Sending you service updates and important notices',
            'Sending marketing emails and promotional offers',
            'Waitlist registration and communications',
            'Recruitment (if you apply for a role)',
            'Resolving technical issues and maintaining the platform',
          ],
        },
      ],
    },
    {
      number: '4',
      title: 'Marketing Communications',
      body: [
        {
          type: 'paragraph',
          text: 'We will only send you marketing emails or promotional content if you have given us your explicit consent to do so. You can opt out at any time by:',
        },
        {
          type: 'list',
          items: [
            'Clicking "unsubscribe" in any marketing email',
            'Updating your preferences in your account settings',
            'Emailing us at support@nsave.com with the subject line "Unsubscribe"',
          ],
        },
        {
          type: 'paragraph',
          text: 'Withdrawing consent for marketing will not affect the delivery of your core services.',
        },
      ],
    },
    {
      number: '6',
      title: 'Who We Share Your Data With',
      body: [
        {
          type: 'paragraph',
          text: 'We do not sell your personal data. We share it only where necessary to provide our services or comply with the law.',
        },
        {
          type: 'paragraph',
          text: 'We work with carefully selected third-party providers for services including identity verification, digital onboarding, IT infrastructure, hosting and platform maintenance. All providers are bound by data processing agreements and are only permitted to process your data on our instructions.',
        },
        {
          type: 'paragraph',
          text: 'We may share your data with law enforcement, regulators or other authorities where we are legally required to do so, or where it is necessary to investigate suspected fraud, illegal activity or a breach of our terms.',
        },
      ],
    },
    {
      number: '7',
      title: 'Your Rights',
      body: [
        {
          type: 'paragraph',
          text: 'Under UK data protection law, you have meaningful rights over your personal data. We take these seriously and will respond to all requests within one calendar month.',
        },
        {
          type: 'table',
          columns: ['Your right', 'What it means'],
          rows: [
            ['Right to access', 'Request a copy of all personal data we hold about you'],
            [
              'Right to rectification',
              'Ask us to correct any inaccurate or incomplete data. You can also update most details directly in your account.',
            ],
            [
              'Right to erasure',
              'Ask us to delete your data where there is no longer a legitimate reason to hold it. Note that some data must be retained to comply with legal obligations.',
            ],
            [
              'Right to restriction',
              'Ask us to pause processing your data while a dispute is resolved or where you contest accuracy.',
            ],
            [
              'Right to object',
              'Object to processing based on legitimate interests or for direct marketing purposes. We will stop unless we have compelling grounds to continue.',
            ],
            [
              'Right to data portability',
              'Receive your data in a structured, machine-readable format and transfer it to another provider, where processing is based on consent or contract.',
            ],
            [
              'Right to withdraw consent',
              'Withdraw consent for any processing that relies on it (e.g. marketing) at any time. This will not affect any processing carried out before withdrawal.',
            ],
          ],
        },
        {
          type: 'paragraph',
          text: 'To exercise any of these rights, email support@nsave.com with the subject line "Personal Data Request".',
        },
        {
          type: 'paragraph',
          text: 'Please note that in some cases, your consent may conflict with a legal obligation or legitimate interest of nsave Ltd as data controller or of a third party.',
        },
      ],
    },
    {
      number: '8',
      title: 'Data Transfer Risk',
      body: [
        {
          type: 'paragraph',
          text: 'While we do everything we reasonably can to protect your data, no internet transmission is completely secure. Please note that data transmitted over public networks (including email) may be accessed by others. We recommend you take care when sharing sensitive information online.',
        },
        {
          type: 'paragraph',
          text: 'If you disclose personal data via a public network or networks operated by third-party providers, you should be aware of the fact that your data may be lost. Also, third parties might gain access and hence collect and use the data without your consent. While many of the individual data packages are transmitted in an encrypted form, the names of the sender and the recipient are not. Third parties can therefore draw conclusions on accounts and business relationships that exist or are being established.',
        },
        {
          type: 'paragraph',
          text: 'Even if the sender and the recipient are residents of the same country, the transmission of data over these networks frequently – and without monitoring – takes place via third countries, so also via countries that do not offer the same degree of protection as the country in which you are domiciled. We do not accept responsibility for the security of your data during transfer via the Internet and reject all liability for direct and indirect damages. The use of other means of communication is permitted if you consider this to be necessary or reasonable for security reasons.',
        },
      ],
    },
    {
      number: '9',
      title: 'Security',
      body: [
        {
          type: 'paragraph',
          text: "Your data security matters to us. We've put in place strong safeguards — including administrative, technical and physical security measures — to keep your personal information protected. That said, no system connected to the internet can be made completely impenetrable, and no method of communication of the internet or method of electronic storage can guarantee complete security.",
        },
      ],
    },
    {
      number: '10',
      title: 'Amendments',
      body: [
        {
          type: 'paragraph',
          text: 'We may update this Privacy Statement from time to time to reflect changes in our services, legal requirements or best practice. The current version will always be available at nsave.com/privacy-statement.',
        },
        {
          type: 'paragraph',
          text: 'Where we make a material change — one that meaningfully affects how your data is used — we will notify you by email in advance of the change taking effect. Where the change affects how we process data for which we rely on your consent, we will seek your fresh consent before proceeding.',
        },
      ],
    },
    {
      number: '11',
      title: 'Links',
      body: [
        {
          type: 'paragraph',
          text: 'Our website may contain links to third-party websites not operated by nsave. We are not responsible for the content or privacy practices of those sites, and this statement does not apply to them. We recommend reading the privacy notice of any website you visit.',
        },
      ],
    },
  ] satisfies PrivacySection[],
  closing: {
    title: 'Questions about this statement?',
    lines: [
      { label: 'Email', value: 'support@nsave.com' },
      { label: 'Post', value: 'Nsave Ltd, 124 City Road, London, EC1V 2NX' },
      { label: 'ICO', value: 'ico.org.uk | 0303 123 1113' },
    ] satisfies PrivacyContactLine[],
  },
};
